import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, SimpleChanges } from "@angular/core";
import { MatDialog } from "@angular/material";
import { AppState } from "@app/reducers";
import { PermissionTypes, currentComponentPageBasedPermissionsSelector$, getSatelliteTileLayer, getTileLayer } from "@app/shared";
import Boundary_Management_Components from "@modules/boundary-management/shared/utils/boundary-management-module.enums";
import { MY_TASK_COMPONENT } from "@modules/my-tasks/shared";
import { BoundaryStatus } from "@modules/sales/shared/utils/sales-module.enums";
import { Store } from "@ngrx/store";
import * as L from "leaflet";
import { Control } from 'leaflet';
import "leaflet-draw";
import { combineLatest, Subscription } from "rxjs";
import { RxjsService } from '../../services/rxjs.services';
import {
	defaultLeafLetMapZoomValue,
	defaultLeafletLatitude,
	defaultLeafletLongitude
} from "../../utils/variables.utils";
import { LeafLetFullMapViewModalComponent } from "./leaf-let-full-map-view.component";

const editedCoordinatesColor = '#ffcccb';
const editedCoordinatesColorBefore = 'red';
const editedCoordinatesColorAfter = 'yellow';
const LAYER_ADD_CHUNK_SIZE = 50;
const LAYER_PREP_CHUNK_SIZE = 30;
@Component({
	selector: "app-shared-leaf-let",
	templateUrl: "./shared-leaf-let.component.html",
	styleUrls: ['./shared-leaf-let.component.scss', '../../../shared/layouts/page-spinner/page-spinner.component.scss']
})
export class SharedLeafLetComponent implements OnInit, OnDestroy {
	@Input() polygonArray;
	@Input() boundaryRequestDetails;
	@Input() isDisabled = false;
	@Input() isResetBtnDisabled = true;
	@Input() isFileUploaded = false;
	@Input() latLong;
	@Input() shouldShowFullViewIcon = true;
	@Input() isUploaded;
	@Input() id = "map_modal";
	@Input() caption = "";
	@Input() subCaption = "";
	@Input() isDisabledFullView = false;
	@Input() shouldEnableDownload = true;
	@Input() shouldShowLegend = true;
	@Output() onSelection = new EventEmitter();
	@Output() onMapSubmitAction = new EventEmitter();
	@Output() afterDataRenderedInMapAction = new EventEmitter();
	@Input() boundaries = [];
	container;
	map;
	drawControl: Control.Draw;
	editableLayers = new L.FeatureGroup();
	openedPopup: L.Popup;
	selectedLayer;
	selectedDownloadableProps = [];
	boundaryStatus = BoundaryStatus;
	createdRectangles = [];
	prevLayerKey;
	markersLayer = new L.LayerGroup();
	shouldHidePolygonDrawShape: boolean;
	isEditAccessDenied = true;
	isLoading = true;
	private permissionsSub: Subscription;
	private anyPropertySub: Subscription;
	private setupTimeoutId: number;
	private _fitBoundsScheduled = false;
	private _isBulkAddingLayers = false;

	constructor(private rxjsService: RxjsService, private dialog: MatDialog, private store: Store<AppState>) {
		this.loaderConfig(true);
	}

	loaderConfig(bool) {
		if (this.isDisabledFullView) {
			this.rxjsService.setPopupLoaderProperty(false);
			this.rxjsService.setGlobalLoaderProperty(false);
		}
		else {
			this.rxjsService.setGlobalLoaderProperty(false);
			this.rxjsService.setPopupLoaderProperty(false);
		}
	}

	ngOnInit(): void {
		this.combineLatestNgrxStoreRolesData();
		this.setupLeafLetMapConfigs();
		this.anyPropertySub = this.rxjsService.getAnyPropertyValue().subscribe((receivedProperty) => {
			if (!receivedProperty) return;
			this.isLoading = receivedProperty.isRequestCreated;
		});
	}

	ngOnDestroy(): void {
		if (this.setupTimeoutId != null) {
			clearTimeout(this.setupTimeoutId);
			this.setupTimeoutId = null;
		}
		if (this.permissionsSub) {
			this.permissionsSub.unsubscribe();
		}
		if (this.anyPropertySub) {
			this.anyPropertySub.unsubscribe();
		}
		if (this.map) {
			if (this.drawControl) {
				try { this.map.removeControl(this.drawControl); } catch (_) { }
			}
			this.map.remove();
			this.map = null;
		}
	}

	combineLatestNgrxStoreRolesData() {
		this.permissionsSub = combineLatest([
			this.store.select(currentComponentPageBasedPermissionsSelector$)]
		).subscribe((response) => {
			let sidebarComponent = this.caption == 'sales' ? Boundary_Management_Components.SALES_BOUNDARIES :
				this.caption == 'technical' ? Boundary_Management_Components.TECHNICAL_BOUNDARIES :
					this.caption == 'operations' ? Boundary_Management_Components.OPERATIONAL_BOUNDARIES :
						this.caption == 'DOAApprovalDashboard' ? MY_TASK_COMPONENT.BOUNDARY_MGMT_APPROVAL_DASHBOARD :
							this.caption == 'MyRequestDashboard' ? MY_TASK_COMPONENT.BOUNDARY_MGMT_VIEW_REQUEST : "";
			this.isEditAccessDenied = response[0][sidebarComponent]?.find(fSC => fSC.menuName === PermissionTypes.EDIT) ? false : true;
		});
	}

	setupLeafLetMapConfigs() {
		if (this.setupTimeoutId != null) {
			clearTimeout(this.setupTimeoutId);
			this.setupTimeoutId = null;
		}
		const mapInitDelay = this.isDisabledFullView ? 120 : 0;
		this.setupTimeoutId = window.setTimeout(() => {
			this.setupTimeoutId = null;
			this.loaderConfig(true);
			// Check if map already exists then remove after that recreate the map
			if (this.map) {
				if (this.drawControl) {
					try { this.map.removeControl(this.drawControl); } catch (_) { }
				}
				this.map.remove();
				this.map = null;
			}
			// Create a container using element's id
			this.container = document.getElementById(this.id);
			// create a map using created element's container using default options like zoom and lat,long values
			this.map = L.map(this.container, { renderer: L.canvas() }).setView(
				[defaultLeafletLatitude, defaultLeafletLongitude],
				defaultLeafLetMapZoomValue
			);

			// create an additional view layer for better representation purpose

			// normal view layer
			let customPaidTileLayer = getTileLayer();
			// Customer's paid view layer
			// let customPaidTileLayer = L.tileLayer(
			// 	"https://api.maps.geoint.africa/v1/" +
			// 	leaflefApiKey +
			// 	"/tile/{x}/{y}/{z}.png?settings=global-decarta_TT"
			// );
			let googleSatelliteViewTileLayer = getSatelliteTileLayer();
			let baseMaps: L.Control.LayersObject = {
				"Normal View": customPaidTileLayer,
				"Satellite View": googleSatelliteViewTileLayer
			};
			L.control.layers(baseMaps, null, { position: 'bottomleft' }).addTo(this.map);
			// Select default view as custom paid layer
			customPaidTileLayer.addTo(this.map);
			if (this.shouldShowLegend) {
				this.onAddLegend();
			}
			// callback event for tile layer once all map related coordinates are successfully rendered into the map
			customPaidTileLayer.on('load', () => {
				if (this.caption.includes('Map View Only')) {
					// setTimeout(() => {
					// 	this.loaderConfig(false);
					// });
				}
				else {
					// this.loaderConfig(false);
				}
			}, this);
			this.map.addLayer(this.editableLayers);
			this.map.addLayer(this.markersLayer);
			if (this.caption == 'Two Table Architecture Exact Address' || this.caption == 'Two Table Architecture Custom Address'
				|| this.caption.includes('Map View Only')) {
				this.prepareDrawableMarker();
			}
			else {
				this.prepareDrawableShapes();
			}
		}, mapInitDelay);
	}

	// draw marker shape only
	prepareDrawableMarker() {
		this.markersLayer.clearLayers();
		if (!this.latLong) {
			this.latLong = { lat: defaultLeafletLatitude, lng: defaultLeafletLongitude };
		}
		else if (this.latLong?.lat && !this.latLong?.lng) {
			this.latLong = { lat: this.latLong.lat, lng: defaultLeafletLongitude };
		}
		else if (!this.latLong?.lat && this.latLong?.lng) {
			this.latLong = { lat: defaultLeafletLatitude, lng: this.latLong.lng };
		}
		this.map.setView([this.latLong.lat, this.latLong.lng], 8);
		L.Icon.Default.mergeOptions({
			iconRetinaUrl: require("../../../../../node_modules/leaflet/dist/images/marker-icon-2x.png"),
			iconUrl: require("../../../../../node_modules/leaflet/dist/images/marker-icon.png"),
			shadowUrl: require("../../../../../node_modules/leaflet/dist/images/marker-shadow.png")
		});
		let marker = L.marker([this.latLong.lat, this.latLong.lng], { draggable: this.caption == 'Map View Only' ? false : true });
		this.markersLayer.addLayer(marker);
		marker.on('dragend', function (e) {
			this.latLong = { lat: marker.getLatLng().lat, lng: marker.getLatLng().lng };
			this.onSelection.emit({ data: { lat: this.latLong.lat, lng: this.latLong.lng } });
		}, this);
		marker.on('click', function (e) {
			this.openedPopup = L.popup()
				.setLatLng(e['latlng'])
				.setContent(`<strong>Latitude : ${e['latlng'].lat.toFixed(6)}</strong> <br> 
									<strong>Longitude : ${e['latlng'].lng.toFixed(6)}</strong>`)
				.openOn(this.map);
		}, this);
		marker.on('mouseout', function (e) {
			setTimeout(() => {
				this.map.closePopup(this.openedPopup);
			}, 1500);
		}, this);
	}

	// draw shapes like polygon, circle, rectangle etc
	prepareDrawableShapes() {
		const downloadEditableLayers = new L.FeatureGroup();
		let polyLayers = [];
		polyLayers = this.polygonArray;
		let currentEditable = null;
		this.preparePolygonShapeToBeShown();
		const layersToAdd: L.Layer[] = [];
		const self = this;
		const prepChunkSize = LAYER_PREP_CHUNK_SIZE;
		const addChunkSize = LAYER_ADD_CHUNK_SIZE;

		const prepareOneLayer = (layer: any, downloadEditableLayersRef: L.FeatureGroup, currentEditableRef: { value: any }) => {
			const feature = (layer.feature = layer.feature || {});
			feature.type = feature.type || "Feature";
			feature.properties = layer.options;
			layer.on('add', (e) => {
				if (layer.options.BatchId) {
					if (layer.options.BatchId == self.boundaryRequestDetails.batchId) {
						L.Icon.Default.mergeOptions({
							iconRetinaUrl: require("../../../../../node_modules/leaflet/dist/images/marker-icon-2x.png"),
							iconUrl: require("../../../../../node_modules/leaflet/dist/images/marker-icon.png"),
							shadowUrl: require("../../../../../node_modules/leaflet/dist/images/marker-shadow.png")
						});
						let marker = L.marker(e.target._bounds._northEast);
						self.markersLayer.addLayer(marker);
						marker.on('mouseover', function (e) {
							this.openedPopup = L.popup()
								.setLatLng(e['latlng'])
								.setContent(`<strong>Recently Created/Updated boundary</strong>`)
								.openOn(self.map);
						}, self);
						marker.on('mouseout', function (e) {
							setTimeout(() => {
								this.map.closePopup(this.openedPopup);
							}, 1500);
						}, self);
						if (!self._isBulkAddingLayers && !self._fitBoundsScheduled) {
							self._fitBoundsScheduled = true;
							const boundsToFit = self._getBoundsToFit();
							requestAnimationFrame(() => {
								self._fitBoundsScheduled = false;
								if (self.map && boundsToFit) {
									try {
										if (self.map.invalidateSize) self.map.invalidateSize();
										self.map.fitBounds(boundsToFit);
									} catch (_) { }
								}
							});
						}
						e.target.setStyle({
							fillColor: self.prepareEditedBoundaryColor(layer.options),
							fillOpacity: 0.3,
							borderColor: self.prepareEditedBoundaryColor(layer.options),
							weight: 3
						});
					}
				}
				else if (layer?.options?.type == 'Bounding Box') {
					self.isResetBtnDisabled = true;
					e.target.setStyle({
						fillColor: 'lightgreen',
						fillOpacity: 0.5,
						borderColor: 'lightgreen',
						weight: 3
					});
					self.prepareDownloadRequestIdsLogic(layer, 'add');
				}
			});
			if (self.map && self.editableLayers) {
				layersToAdd.push(layer);
			}
			layer.on("click", (e) => {
				if (self.isDisabled || e.target.feature.properties.BatchId !== self.boundaryRequestDetails.batchId || self.shouldHidePolygonDrawShape) {
					return;
				}
				currentEditableRef.value = e.target;
				let isEditable = downloadEditableLayersRef.getLayer(e.target._leaflet_id) == undefined;
				if (downloadEditableLayersRef.getLayer(e.target._leaflet_id) != undefined) {
					currentEditableRef.value.editing.disable();
					downloadEditableLayersRef.removeLayer(e.target);
				}
				if (isEditable && self.caption !== 'GISDashboard' && self.caption !== 'DOAApprovalDashboard') {
					e.target.editing.enable();
					downloadEditableLayersRef.addLayer(currentEditableRef.value);
				}
			}, self);
			layer.on('mouseover', (e) => {
				self.selectedLayer = layer;
				self.onLayerMouseOver(e);
			}, self);
			layer.on('mouseout', self.onLayerMouseOut, self);
		};

		const currentEditableRef = { value: null as L.Layer };
		const runPrepChunk = (startIndex: number) => {
			const end = Math.min(startIndex + prepChunkSize, polyLayers.length);
			for (let i = startIndex; i < end; i++) {
				let layer = polyLayers[i];
				prepareOneLayer(layer, downloadEditableLayers, currentEditableRef);
			}
			if (end < polyLayers.length) {
				requestAnimationFrame(() => runPrepChunk(end));
			} else if (layersToAdd.length > 0) {
				const addNextChunk = (addStartIndex: number) => {
					if (addStartIndex === 0) {
						self._isBulkAddingLayers = true;
					}
					const addEnd = Math.min(addStartIndex + addChunkSize, layersToAdd.length);
					for (let i = addStartIndex; i < addEnd; i++) {
						self.editableLayers.addLayer(layersToAdd[i]);
					}
					if (addEnd < layersToAdd.length) {
						requestAnimationFrame(() => addNextChunk(addEnd));
					} else {
						self._isBulkAddingLayers = false;
						const boundsToFit = self._getBoundsToFit();
						const doFitAndEmit = () => {
							if (self.map && boundsToFit) {
								try {
									if (self.map.invalidateSize) self.map.invalidateSize();
									self.map.fitBounds(boundsToFit);
								} catch (_) { }
							}
							self.afterDataRenderedInMapAction.emit(true);
							self.isLoading = false;
							self.rxjsService.setAnyPropertyValue(null);
						};
						if (self.isDisabledFullView) {
							requestAnimationFrame(() => {
								if (self.map && self.map.invalidateSize) self.map.invalidateSize();
								requestAnimationFrame(doFitAndEmit);
							});
						} else {
							requestAnimationFrame(doFitAndEmit);
						}
					}
				};
				requestAnimationFrame(() => addNextChunk(0));
			} else {
				self.afterDataRenderedInMapAction.emit(true);
				self.isLoading = false;
				self.rxjsService.setAnyPropertyValue(null);
			}
		};

		if (polyLayers.length > 0 && this.map && this.editableLayers) {
			requestAnimationFrame(() => runPrepChunk(0));
		} else {
			this.afterDataRenderedInMapAction.emit(true);
			this.isLoading = false;
			this.rxjsService.setAnyPropertyValue(null);
		}
		this.drawControl = new L.Control.Draw({
			draw: {
				circle: false,
				polygon: this.shouldHidePolygonDrawShape ? false : {
					allowIntersection: false, // Restricts shapes to simple polygons
					drawError: {
						color: "#e1e100", // Color the shape will turn when intersects
						message: "<strong>We cannot draw the intersection among the polygons<strong>", // Message that will show when intersect
					},
					shapeOptions: {
						color: "#97009c",                     //   color code newly created polygon
					},
				},
				polyline: false,
				marker: false,
				circlemarker: false,
				rectangle: this.caption == 'GISDashboard' && !this.shouldShowFullViewIcon ? {
					metric: false,                                     // to enable the rectangle is drawn correctly without any error
					shapeOptions: { color: 'lightgreen' }
				} : false,
			},
			edit: {
				edit: false,
				featureGroup: this.editableLayers,
				remove: false                                      // enable delete option if the option is commented or disable delete option if the value is null
			},
		});
		// add custom tooltip on hovering rectangle shape
		L.drawLocal.draw.toolbar.buttons.rectangle = 'Bounding box';
		// Add draw control to leaflet map if the component is enabled only
		if (!this.isDisabled) {
			this.map.addControl(this.drawControl);
		}
		this.map.on(L.Draw.Event.CREATED, (e) => {
			// total actions for newly created polygon
			if (e.layerType == 'polygon') {
				let type = e["layerType"],
					layer = e["layer"],
					feature = (layer.feature = layer.feature || {});
				feature.type = feature.type || "Feature";
				let props = (feature.properties = feature.properties || {});
				// create custom props 
				props.type = 'create'
				props.Name = "New Boundary";
				props.Id = `TEMP-NO${Math.floor((Math.random() * 10000000000) + 1)}`;   // random number for polygon identity create process
				props.BatchId = null;
				this.editableLayers.addLayer(layer);
				selectedData(this);
				let selectedFeature = null;
				layer.on("click", (e) => {
					let isEditable = downloadEditableLayers.getLayer(e.target._leaflet_id) == undefined;
					if (downloadEditableLayers.getLayer(e.target._leaflet_id) != undefined) {
						selectedFeature.editing.disable();
						downloadEditableLayers.removeLayer(e.target);
					}
					if (isEditable) {
						selectedFeature = e.target;
						e.target.editing.enable();
						downloadEditableLayers.addLayer(selectedFeature);
					}
				});
				layer.on('mouseover', (e) => {
					this.selectedLayer = layer;
					this.onLayerMouseOver(e)
				}, this);
				layer.on('mouseout', this.onLayerMouseOut, this);
				let data = this.editableLayers.toGeoJSON();
				L.geoJSON(data, {
					onEachFeature: (feature, layer) => {
						layer.bindPopup(feature.properties.description);
					},
				});
			}
			// total actions for newly created rectangle ( this shape is created for only downloading drawn area coordinates ) 
			else if (e.layerType == 'rectangle') {
				let type = e["layerType"],
					layer = e["layer"],
					feature = (layer.feature = layer.feature || {});
				feature.type = feature.type || "Feature";
				let props = (feature.properties = feature.properties || {});
				// create custom props 
				props.type = 'create rectangle';
				props.Id = `TEMP-NO-RECTANGLE${Math.floor((Math.random() * 10000000000) + 1)}`;   // random number for polygon identity create process
				this.editableLayers.addLayer(layer);
				this.prepareDownloadRequestIdsLogic(layer, 'create');
			}
		}, this);

		// emit properties on every single adjustments of edited polygon's coordinates
		this.map.on(L.Draw.Event.EDITVERTEX, (e) => {
			this.onSelection.emit({ data: e.poly.toGeoJSON() });
		}, this);

		// emit properties of all after deletion of selected polygons
		this.map.on(L.Draw.Event.DELETED, (e) => {
			this.onSelection.emit({ data: this.editableLayers.toGeoJSON() });
		}, this);

		// once all latitude and longitude is collected then emit all data to parent component ( only one drawn shape's coordinates )
		function selectedData(that) {
			that.onSelection.emit({ data: that.editableLayers.toGeoJSON() });
		}
	}

	_getBoundsToFit(): L.LatLngBounds | null {
		if (!this.editableLayers) return null;
		const batchId = this.boundaryRequestDetails?.batchId;
		let combined: L.LatLngBounds | null = null;
		if (batchId != null && batchId !== undefined && batchId !== '') {
			this.editableLayers.eachLayer((layer: any) => {
				if (layer.options?.BatchId === batchId && layer.getBounds) {
					try {
						const b = layer.getBounds();
						if (b && b.getSouthWest && b.getNorthEast) {
							if (!combined) combined = L.latLngBounds(b.getSouthWest(), b.getNorthEast());
							else combined.extend(b);
						}
					} catch (_) { }
				}
			});
		}
		if (!combined && this.editableLayers.getBounds) {
			try { combined = this.editableLayers.getBounds(); } catch (_) { }
		}
		return combined || null;
	}

	prepareEditedBoundaryColor(props): string {
		switch (true) {
			case !props?.modificationType:
				return editedCoordinatesColor;
			case props?.modificationType == 'Before':
				return editedCoordinatesColorBefore;
			case props?.modificationType == 'After':
				return editedCoordinatesColorAfter;
		}
	}

	preparePolygonShapeToBeShown() {
		if (this.shouldShowFullViewIcon) {
			this.shouldHidePolygonDrawShape = true;
		}
		else if (this.caption == 'LSS Boundary' && this.boundaryRequestDetails?.boundaryStatusId == null) {
			this.shouldHidePolygonDrawShape = false;
		}
		else if (this.caption == 'GISDashboard' || this.subCaption ||
			(this.boundaryRequestDetails?.boundaryStatusId !== BoundaryStatus.INITIATED &&
				this.caption !== 'sales' && this.caption !== 'technical' && this.caption !== 'operations' &&
				this.boundaryRequestDetails?.boundaryStatusId !== BoundaryStatus.REJECTED) ||
			(this.boundaryRequestDetails?.boundaryStatusId !== BoundaryStatus.LIVE &&
				(this.caption == 'sales' || this.caption == 'technical' || this.caption == 'operations'))) {
			this.shouldHidePolygonDrawShape = true;
		}
	}

	createdOrAddedRectangleLayer;
	prepareDownloadRequestIdsLogic(layer, type: string) {
		Object.keys(this.editableLayers['_layers']).forEach((layerKey) => {
			if (type == 'create' && this.editableLayers['_layers']?.[layerKey]?.['feature']?.['properties']?.['Id'] == layer['feature']?.['properties']?.['Id']) {
				delete this.editableLayers['_layers']?.[layerKey];
			}
			if (this.editableLayers['_layers']?.[layerKey]?.['feature']?.['properties']?.['type'] == "create rectangle" && type == 'create') {
				// prepare created rectangle's lat and long
				let rectangle = { name: 'rectangle', bounds: [], coordinates: {}, Id: "" };
				let geoJson = this.editableLayers['_layers'][layerKey].toGeoJSON();
				rectangle.coordinates = geoJson['geometry']['coordinates'];
				rectangle.Id = geoJson['properties']['Id'];
				this.editableLayers['_layers'][layerKey]._latlngs[0].forEach((latLng: L.LatLng) => {
					rectangle.bounds.push({ x: latLng.lng, y: latLng.lat });
				});
				this.createdOrAddedRectangleLayer = this.editableLayers['_layers'][layerKey];
				this.createdRectangles.push(rectangle);
			}
			// prepare created rectangle's lat and long
			else if (this.editableLayers['_layers']?.[layerKey]?.['feature']?.['properties']?.['type'] == "Bounding Box" && type == 'add') {
				if (!this.prevLayerKey || this.prevLayerKey !== layerKey) {
					let rectangle = { name: 'rectangle', bounds: [], coordinates: {}, Id: "" };
					let geoJson = this.editableLayers['_layers'][layerKey].toGeoJSON();
					rectangle.coordinates = geoJson['geometry']['coordinates'];
					rectangle.Id = `TEMP-NO-RECTANGLE${Math.floor((Math.random() * 10000000000) + 1)}`;
					this.editableLayers['_layers'][layerKey]._latlngs[0].forEach((latLng: L.LatLng) => {
						rectangle.bounds.push({ x: latLng.lng, y: latLng.lat });
					});
					this.createdOrAddedRectangleLayer = this.editableLayers['_layers'][layerKey];
					this.createdRectangles.push(rectangle);
					this.prevLayerKey = layerKey;
				}
			}
		});
		// Loop through all layers and prepare lat long and its props to push to isFallenTestCoorinates
		Object.keys(this.editableLayers['_layers']).forEach((layerId, index) => {
			let filteredLayer = this.editableLayers['_layers'][layerId];
			let filteredLayerFeatureProps = filteredLayer.feature.properties;
			if (!filteredLayerFeatureProps?.type?.includes('create rectangle')) {
				if (this.createdOrAddedRectangleLayer.getBounds().contains(new L.LatLng(filteredLayer._bounds._northEast.lat,
					filteredLayer._bounds._northEast.lng))) {
					if (this.createdOrAddedRectangleLayer.getBounds().contains(new L.LatLng(filteredLayer._bounds._southWest.lat,
						filteredLayer._bounds._southWest.lng))) {
						if (filteredLayerFeatureProps?.['modificationType']) {
							if (filteredLayerFeatureProps?.['modificationType'] == 'After') {
								if (filteredLayerFeatureProps?.boundaryRequestId) {
									this.selectedDownloadableProps.push(filteredLayerFeatureProps.boundaryRequestId);
								}
							}
						}
						else if (filteredLayerFeatureProps?.boundaryRequestId) {
							this.selectedDownloadableProps.push(filteredLayerFeatureProps.boundaryRequestId);
						}
						if (Object.keys(this.editableLayers['_layers']).length == index + 1) {
							this.onDownloadableBoundaryRequestIdsEmitEvent(type);
						}
					}
				}
			}
			else {
				this.onDownloadableBoundaryRequestIdsEmitEvent(type);
			}
		});
	}

	onDownloadableBoundaryRequestIdsEmitEvent(type: string) {
		this.onSelection.emit({
			selectedDownloadableProps: [...new Set(this.selectedDownloadableProps)],
			boundingBoxRectangles: this.createdRectangles, type
		});
	}

	onAddLegend() {
		let legend = L.control.attribution({ position: "topright" });
		legend.onAdd = (map) => {
			let div = L.DomUtil.create('div', 'info legend');
			let labels = [],
				categories = this.caption == 'GISDashboard' ? ['New Boundary', 'Pending - Go Live', 'Approved Boundaries', 'Bounding Box'] :
					['New Boundary', 'Pending - Go Live', 'Approved Boundaries'];
			for (let i = 0; i < categories.length; i++) {
				div.innerHTML +=
					labels.push(
						`<i class="fa fa-square" aria-hidden="true" style="color:${this.getColorForLegend(categories[i])};
						fillOpacity:1"></i>
						${categories[i]}`);
			}
			div.innerHTML = labels.join('<br>');
			return div;
		};
		legend.addTo(this.map);
	}

	getColorForLegend(d) {
		return d === 'New Boundary' ? "#97009c" :
			d === 'Pending - Go Live' ? editedCoordinatesColor :
				d === 'Approved Boundaries' ? "#3388ff" : d === 'Bounding Box' ? 'lightgreen' : '#000';
	}

	onLayerMouseOver(e) {
		if (this.map && e?.target?.options?.type !== 'Bounding Box') {
			this.openedPopup = L.popup()
				.setLatLng(L.latLng(e.latlng.lat, e.latlng.lng))
				.setContent(`<strong>${e.target.feature.properties.Name}</strong>`)
				.openOn(this.map);
			let setHighlightStyle = {};
			// let groupByBatchIdColor = e.target.options.BatchId &&
			// 	this.groupByBatchIds.find(g => g['batchId'] == e.target.options.BatchId).fillColor;

			// edited polygon
			if (e.target.hasOwnProperty('edited') && e.target.edited && e.target.options.Id) {
				setHighlightStyle = {
					fillColor: this.prepareEditedBoundaryColor(e.target.options),
					color: this.prepareEditedBoundaryColor(e.target.options),
					fillOpacity: 0.3,
					weight: 6,
					borderColor: this.prepareEditedBoundaryColor(e.target.options)
				}
			}
			// newly created polygon
			else if (e.target.feature.properties.hasOwnProperty('type')) {
				setHighlightStyle = {
					fillColor: '#97009c',
					fillOpacity: 0.3,
					weight: 6
				};
			}
			// existing polygon
			else {
				setHighlightStyle = {
					fillColor: (e.target.feature.properties.fillColor == this.prepareEditedBoundaryColor(e.target.options)) ? e.target.feature.properties.fillColor : '#3388ff',
					fillOpacity: 0.3,
					weight: 6
				}
			}
			e.target.setStyle(setHighlightStyle);
		}
		// bounding box
		if (e?.target?.options?.type == 'Bounding Box') {
			e.target.setStyle({
				fillColor: 'lightgreen',
				color: 'lightgreen',
				fillOpacity: 0.3,
				weight: 6,
				borderColor: 'lightgreen'
			});
		}
	}

	onLayerMouseOut(e) {
		if (this.openedPopup && this.map) {
			this.map.closePopup(this.openedPopup);
			let resetHighlighedStyle = {};
			// let groupByBatchIdColor = e.target.options.BatchId &&
			// 	this.groupByBatchIds.find(g => g['batchId'] == e.target.options.BatchId).fillColor;
			// edited polygon
			if (e.target.hasOwnProperty('edited') && e.target.edited && e.target.options.Id) {
				resetHighlighedStyle = {
					fillColor: this.prepareEditedBoundaryColor(e.target.options),
					color: this.prepareEditedBoundaryColor(e.target.options),
					weight: 3,
					borderColor: this.prepareEditedBoundaryColor(e.target.options)
				}
			}
			// newly created polygon
			else if (e.target.feature.properties.hasOwnProperty('type')) {
				resetHighlighedStyle = {
					fillColor: '#97009c',
					weight: 3
				};
			}
			// existing polygon
			else {
				resetHighlighedStyle = {
					fillColor: (e.target.feature.properties.fillColor == this.prepareEditedBoundaryColor(e.target.options)) ?
						e.target.feature.properties.fillColor : '#3388ff',
					weight: 3
				}
			}
			e.target.setStyle(resetHighlighedStyle);
			this.openedPopup = null;
			//this.selectedLayer = null;
		}
		// bounding box
		if (e?.target?.options?.type == 'Bounding Box') {
			e.target.setStyle({
				fillColor: 'lightgreen',
				color: 'lightgreen',
				weight: 3,
				borderColor: 'lightgreen'
			});
		}
	}

	ngOnChanges(changes: SimpleChanges) {
		for (let prop in changes) {
			switch (prop) {
				case "polygonArray":
					this.editableLayers.clearLayers();
					this.markersLayer.clearLayers();
					this.setupLeafLetMapConfigs();
					break;
				case "isUploaded":
					if (this.isUploaded) {
						this.onSelection.emit({ data: this.editableLayers.toGeoJSON() });
					}
					break;
				case "shouldShowFullViewIcon":
					// remove later this logic
					if (this.shouldShowFullViewIcon) {
						this.isDisabled = true;
					}
					else {
						this.isDisabled = false;
					}
					break;
			}
		}

		if (!this.isDisabled && this.map && this.drawControl) {
			this.map.addControl(this.drawControl);
		} else if (!this.isDisabled && this.isFileUploaded && !this.map) {
			this.ngOnInit();
		}
	}

	openFullMapViewWithConfig() {
		// if (this.isEditAccessDenied) {
		// 	this.snackbarService.openSnackbar(PERMISSION_RESTRICTION_ERROR, ResponseMessageTypes.WARNING);
		// }
		// else {
		this.rxjsService.setDialogOpenProperty(true);
		const dialogReff = this.dialog.open(LeafLetFullMapViewModalComponent, {
			width: "100vw",
			maxHeight: "100vh",
			disableClose: true,
			data: this.subCaption ? {
				polygonArray: this.polygonArray,
				subCaption: this.subCaption
			} :
				{
					fromUrl: this.caption,
					boundaryRequestId: this.boundaryRequestDetails.boundaryRequestId,
					boundaryRequestRefNo: this.boundaryRequestDetails.boundaryRequestRefNo ?
						this.boundaryRequestDetails.boundaryRequestRefNo : this.boundaryRequestDetails.boundaryReqRefNo,
					isDisabled: this.isDisabled,
					boundaryRequestDetails: this.boundaryRequestDetails,
					boundaries: this.boundaries
				},
		});
		dialogReff.afterClosed().subscribe((result) => {
			if (result?.hasOwnProperty('boundaries')) {
				this.onMapSubmitAction.emit(result);
			}
			this.rxjsService.setGlobalLoaderProperty(false);
		});
	}
}
